import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CreditCardProfileListViewModel } from '../models/CreditCardProfileListViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { EtkViewModel } from '../models/EtkViewModel';
import type { InvoiceAddressAddViewModel } from '../models/InvoiceAddressAddViewModel';
import type { InvoiceAddressUpdateViewModel } from '../models/InvoiceAddressUpdateViewModel';
import type { InvoiceAddressViewModel } from '../models/InvoiceAddressViewModel';
import type { RegisteredPassengerAddViewModel } from '../models/RegisteredPassengerAddViewModel';
import type { RegisteredPassengerUpdateViewModel } from '../models/RegisteredPassengerUpdateViewModel';
import type { RegisteredPassengerViewModel } from '../models/RegisteredPassengerViewModel';
import type { SalesMasterViewModel } from '../models/SalesMasterViewModel';
import type { UserUpdateViewModel } from '../models/UserUpdateViewModel';
import type { UserUpsertRequestViewModel } from '../models/UserUpsertRequestViewModel';
import type { UserViewModel } from '../models/UserViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class UsersService {
  /**
   * Profile
   * Gets profile by token
   * @returns UserViewModel Success
   * @throws ApiError
   */
  public static async getUser(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<UserViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<UserViewModel>({
      method: 'GET',
      url: `/users`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Profile
   * Updates profile by token
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async updateUser(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: UserUpdateViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PUT',
      url: `/users`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Reservations
   * Gets all reservations by token
   * @returns SalesMasterViewModel Success
   * @throws ApiError
   */
  public static async listReservations(
    {
      authorization,
      page,
      size,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<SalesMasterViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<SalesMasterViewModel>>({
      method: 'GET',
      url: `/users/reservations`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      query: {
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Update ETK Settings
   * Updates etk settings
   * @returns EtkViewModel Success
   * @throws ApiError
   */
  public static async updateEtk(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: UserUpsertRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EtkViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<EtkViewModel>({
      method: 'PUT',
      url: `/users/etk`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Invoice Addresses
   * Get Invoice Address by UserId
   * @returns InvoiceAddressViewModel Success
   * @throws ApiError
   */
  public static async getInvoiceAddresses(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<InvoiceAddressViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<InvoiceAddressViewModel>>({
      method: 'GET',
      url: `/users/invoice-address-list`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Invoice Address
   * Get Invoice Address by uId
   * @returns InvoiceAddressViewModel Success
   * @throws ApiError
   */
  public static async getInvoiceAddressByUId(
    {
      authorization,
      uId,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      /**
       * uId
       */
      uId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<InvoiceAddressViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<InvoiceAddressViewModel>({
      method: 'GET',
      url: `/users/invoice-address/${uId}`,
      path: {
        ...(uId ? { uId: uId } : {}),
      },
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Add Invoice Address
   * Add Invoice Address
   * @returns InvoiceAddressViewModel Success
   * @throws ApiError
   */
  public static async addInvoiceAddress(
    {
      body,
    }: {
      body?: InvoiceAddressAddViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<InvoiceAddressViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<InvoiceAddressViewModel>({
      method: 'POST',
      url: `/users/invoice-address`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Update Invoice Address
   * Update Invoice Address
   * @returns boolean Success
   * @throws ApiError
   */
  public static async updateInvoiceAddress(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: InvoiceAddressUpdateViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<boolean> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<boolean>({
      method: 'PUT',
      url: `/users/invoice-address`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(headers, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Delete Invoice Address
   * Delete Invoice Address by UId
   * @returns boolean Success
   * @throws ApiError
   */
  public static async deleteInvoiceAddressByUId(
    {
      uid,
    }: {
      /**
       * UId
       */
      uid: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<boolean> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<boolean>({
      method: 'DELETE',
      url: `/users/invoice-address`,
      query: {
        ...(uid ? { uid: uid } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Registered Passengers
   * Get Registered Passengers by UserId
   * @returns RegisteredPassengerViewModel Success
   * @throws ApiError
   */
  public static async getRegisteredPassengers(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<RegisteredPassengerViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<Array<RegisteredPassengerViewModel>>({
      method: 'GET',
      url: `/users/registered-passenger-list`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Registered Passenger
   * Get Registered Passenger by Unique Id
   * @returns RegisteredPassengerViewModel Success
   * @throws ApiError
   */
  public static async getRegisteredPassenger(
    {
      uId,
    }: {
      /**
       * uId
       */
      uId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RegisteredPassengerViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<RegisteredPassengerViewModel>({
      method: 'GET',
      url: `/users/registered-passenger/${uId}`,
      path: {
        ...(uId ? { uId: uId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Registered Passenger Add
   * Add Registered Passenger
   * @returns RegisteredPassengerViewModel Success
   * @throws ApiError
   */
  public static async addRegisteredPassenger(
    {
      body,
    }: {
      body?: RegisteredPassengerAddViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RegisteredPassengerViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<RegisteredPassengerViewModel>({
      method: 'POST',
      url: `/users/registered-passenger`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Registered Passenger Update
   * Update Registered Passenger
   * @returns boolean Success
   * @throws ApiError
   */
  public static async updateRegisteredPassenger(
    {
      body,
    }: {
      body?: RegisteredPassengerUpdateViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<boolean> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<boolean>({
      method: 'PUT',
      url: `/users/registered-passenger`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Registered Passenger Delete
   * Delete Registered Passenger by Unique Id
   * @returns boolean Success
   * @throws ApiError
   */
  public static async deleteRegisteredPassenger(
    {
      uid,
    }: {
      /**
       * UId
       */
      uid: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<boolean> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<boolean>({
      method: 'DELETE',
      url: `/users/registered-passenger`,
      query: {
        ...(uid ? { uid: uid } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Credit Cards
   * Get Credit Cards
   * @returns CreditCardProfileListViewModel Success
   * @throws ApiError
   */
  public static async getCreditCardProfile(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CreditCardProfileListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CreditCardProfileListViewModel>({
      method: 'GET',
      url: `/users/credit-cards`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Garanti Service Test
   * Get Garanti Auth
   * @returns any Success
   * @throws ApiError
   */
  public static async getGarantiAuth(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<any> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<any>({
      method: 'GET',
      url: `/users/garanti-service-test`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
